import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { usePaymentStatus } from '@/features/orders/api/use-orders'
import { useEffect, useState } from 'react'
import { CheckCircle2, XCircle, Loader2, ShoppingBag, ArrowRight } from 'lucide-react'
import { Button } from '@/shared/ui'
import { AuthGuard } from '@/shared/lib/router'
import { useTelegram } from '@/shared/lib/telegram'

const searchSchema = z.object({
  orderId: z.string().optional(),
})

export const Route = createFileRoute('/payment-result')({
  validateSearch: searchSchema,
  component: PaymentResultPage,
})

function PaymentResultPage() {
  return (
    <AuthGuard>
      <PaymentResultContent />
    </AuthGuard>
  )
}

function PaymentResultContent() {
  const { orderId } = Route.useSearch()
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const [timeoutReached, setTimeoutReached] = useState(false)
  const [stopPolling, setStopPolling] = useState(false)

  const { data } = usePaymentStatus(orderId, !stopPolling)

  const isPaid = data?.paymentStatus === 'succeeded'
  const isCanceled = data?.paymentStatus === 'canceled'

  // Останавливаем поллинг при финальном статусе
  useEffect(() => {
    if (isPaid || isCanceled) {
      setStopPolling(true)
    }
  }, [isPaid, isCanceled])

  // Таймаут поллинга — 60 сек
  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeoutReached(true)
      setStopPolling(true)
    }, 60000)
    return () => clearTimeout(timer)
  }, [])

  // Авторедирект после успешной оплаты
  useEffect(() => {
    if (isPaid && orderId) {
      hapticFeedback?.notificationOccurred('success')
      const timer = setTimeout(() => {
        navigate({ to: '/order/$orderId', params: { orderId } })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isPaid, orderId, navigate, hapticFeedback])

  // Haptic при ошибке
  useEffect(() => {
    if (isCanceled) {
      hapticFeedback?.notificationOccurred('error')
    }
  }, [isCanceled, hapticFeedback])

  // Загрузка
  if (!isPaid && !isCanceled && !timeoutReached) {
    return (
      <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6 max-w-md animate-in fade-in duration-300">
          <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto" />
          <div>
            <h1 className="text-xl font-bold text-ink dark:text-white mb-2">
              Проверяем оплату...
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Это займёт несколько секунд
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Успех
  if (isPaid) {
    return (
      <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6 max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex justify-center">
            <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-in zoom-in duration-300">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink dark:text-white mb-2">
              Оплата прошла!
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Ваш заказ оплачен и передан в обработку
            </p>
          </div>
          <Button
            className="w-full"
            onClick={() => orderId && navigate({ to: '/order/$orderId', params: { orderId } })}
          >
            Перейти к заказу
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    )
  }

  // Отмена
  if (isCanceled) {
    return (
      <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6 max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex justify-center">
            <div className="h-20 w-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink dark:text-white mb-2">
              Оплата не прошла
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Платёж был отменён. Вы можете оформить заказ повторно.
            </p>
          </div>
          <Button
            className="w-full"
            onClick={() => navigate({ to: '/catalog/$section', params: { section: 'tea' } })}
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            В каталог
          </Button>
        </div>
      </div>
    )
  }

  // Таймаут
  return (
    <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-6 max-w-md animate-in fade-in duration-300">
        <div className="flex justify-center">
          <div className="h-20 w-20 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Loader2 className="h-12 w-12 text-amber-500" />
          </div>
        </div>
        <div>
          <h1 className="text-xl font-bold text-ink dark:text-white mb-2">
            Не удалось подтвердить оплату
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Статус оплаты можно проверить в разделе «Заказы»
          </p>
        </div>
        <div className="space-y-3 w-full">
          <Button
            className="w-full"
            onClick={() => navigate({ to: '/orders' })}
          >
            Мои заказы
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => navigate({ to: '/catalog/$section', params: { section: 'tea' } })}
          >
            В каталог
          </Button>
        </div>
      </div>
    </div>
  )
}
